import {doQuerySafe, doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {resolveDraftPropertyFromSmiles} from 'notebook/integrations/chemistry/custom-evals/DraftChemicalPropertiesCustomEval';
import {getVersionedBioentityFromMoleculeVersionCell} from 'notebook/integrations/chemistry/custom-evals/MoleculeVersionCustomEval';
import type {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetMolecularWeightQuery as GetMolecularWeightQueryResponse,
  GetMolecularWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetMolecularWeightQuery';
import {
  GetVersionedMolecularWeightQuery as GetVersionedMolecularWeightQueryResponse,
  GetVersionedMolecularWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetVersionedMolecularWeightQuery';
import {
  GET_MOLECULAR_WEIGHT_QUERY,
  GET_VERSIONED_MOLECULAR_WEIGHT_QUERY,
} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  DraftChemicalProperty,
  GetMolecularWeightQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';

/**
 * Preload unit types into Apollo cache. This populates UnitBySymbolNode entries
 * via the copyIncomingUnitsToSymbolCache type policy, making unit data available
 * for stoichiometry calculations from molecular weight column.
 *
 * Fire-and-forget - we don't need to wait for the result.
 */
function preloadUnitTypes(): void {
  // Apollo will cache the result and not re-fetch on subsequent calls
  doQuerySafe<UnitTypesQuery>({query: UNIT_TYPES_QUERY}, {}).catch(() => {
    // Silently ignore errors - unit preloading is best-effort
  });
}

/** Molecular weight keyed by `${moleculeId}:${versionId}`, for queries that specify a version. */
async function fetchVersionedMolecularWeightByKey(
  queries: Array<GetMolecularWeightQuery & {versionId: string}>
): Promise<Map<string, number | null>> {
  const moleculeVersionSpecifiers = queries.map((query) => ({
    moleculeId: query.moleculeId,
    versionId: query.versionId,
  }));

  const queryResult = await doQueryWithoutCache<
    GetVersionedMolecularWeightQueryResponse,
    GetVersionedMolecularWeightQueryVariables
  >({query: GET_VERSIONED_MOLECULAR_WEIGHT_QUERY}, {variables: {moleculeVersionSpecifiers}});

  // Order-aligned with moleculeVersionSpecifiers (Promise.all in the resolver preserves order), so
  // zip by position rather than re-deriving a shared key -- a versionId is not enough on its own to
  // disambiguate which specifier a result belongs to when a batch mixes a valid pair with a stale
  // one that happens to reuse the same versionId under a different moleculeId.
  const molecularWeightByKey = new Map<string, number | null>();
  queryResult.moleculeVersions.forEach((moleculeVersion, index) => {
    const {moleculeId, versionId} = moleculeVersionSpecifiers[index];
    const molecularWeight = isInaccessible(moleculeVersion)
      ? null
      : moleculeVersion.chemicalStructure.molecularWeight.__typename === 'MolecularWeightNode'
        ? moleculeVersion.chemicalStructure.molecularWeight.value
        : null;
    molecularWeightByKey.set(`${moleculeId}:${versionId}`, molecularWeight);
  });
  return molecularWeightByKey;
}

/** Molecular weight keyed by moleculeId, from the molecule's current version -- the fallback for
 * queries that predate the Molecule Version column (no versionId to pin to). */
async function fetchCurrentVersionMolecularWeightByMoleculeId(
  moleculeIds: Array<string>
): Promise<Map<string, number | null>> {
  const queryResult = await doQueryWithoutCache<
    GetMolecularWeightQueryResponse,
    GetMolecularWeightQueryVariables
  >({query: GET_MOLECULAR_WEIGHT_QUERY}, {variables: {moleculeIds}});

  const molecularWeightByMoleculeId = new Map<string, number | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      molecularWeightByMoleculeId.set(molecule.invalidId, null);
    } else {
      const molecularWeight = molecule.currentVersion.chemicalStructure.molecularWeight;
      molecularWeightByMoleculeId.set(
        molecule.id,
        molecularWeight.__typename === 'MolecularWeightNode' ? molecularWeight.value : null
      );
    }
  }
  return molecularWeightByMoleculeId;
}

export async function bulkResolveGetMolecularWeightToServerResolutionResult(
  queries: Array<GetMolecularWeightQuery>
): Promise<Array<ServerResolutionResult>> {
  // Preload unit types in the background so they're available for stoichiometry calculations
  preloadUnitTypes();

  const versionedQueries = queries.filter(
    (query): query is GetMolecularWeightQuery & {versionId: string} => query.versionId != null
  );
  const unversionedMoleculeIds = queries
    .filter((query) => query.versionId == null)
    .map((query) => query.moleculeId);

  const [molecularWeightByKey, molecularWeightByMoleculeId] = await Promise.all([
    versionedQueries.length > 0
      ? fetchVersionedMolecularWeightByKey(versionedQueries)
      : Promise.resolve(new Map<string, number | null>()),
    unversionedMoleculeIds.length > 0
      ? fetchCurrentVersionMolecularWeightByMoleculeId(unversionedMoleculeIds)
      : Promise.resolve(new Map<string, number | null>()),
  ]);

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const molecularWeight =
      query.versionId != null
        ? molecularWeightByKey.get(`${query.moleculeId}:${query.versionId}`)
        : molecularWeightByMoleculeId.get(query.moleculeId);
    if (molecularWeight == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve molecular weight');
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.FLOAT,
      value: String(molecularWeight),
      rawValue: String(molecularWeight),
      floatValue: molecularWeight,
    });
  });
}

export function getMolecularWeightColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];

  // If there's no bioentity in the dependency, the row may be an unrecognized (draft) molecule:
  // resolve the molecular weight directly from its canonical SMILES when present.
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return resolveDraftPropertyFromSmiles(
      typedValue,
      customEvalOptions,
      DraftChemicalProperty.MOLECULAR_WEIGHT,
      resolveFromResolutionQuery
    );
  }

  const moleculeId = bioentityTypedValue.bioentitySummary.id;

  // The Molecule Version cell (second dependency) is undefined on tables predating that column;
  // resolveFromResolutionQuery falls back to the molecule's current version in that case. It's also
  // absent/unresolved for Lot-derived rows (which never populate this column) or freshly-added rows
  // whose bioentity hasn't resolved yet -- getVersionedBioentityFromMoleculeVersionCell returns null
  // for all of those cases, same as for a legacy plain-string version id.
  const moleculeVersionTypedValue = cellDependencies[1];
  const versionId = getVersionedBioentityFromMoleculeVersionCell(moleculeVersionTypedValue)?.versionApiId;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULAR_WEIGHT,
      moleculeId,
      versionId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
